import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Database, Download, Trash2, RotateCcw, Play, ShieldCheck, Clock, HardDriveDownload, CloudUpload, ListChecks, AlertTriangle, Mail, UploadCloud, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtSize = (b) => { if (!b) return "—"; const mb = b / 1048576; return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`; };
const TIPO_LABEL = { manuale: "Manuale", settimanale: "Automatico", "pre-ripristino": "Pre-ripristino" };
const TIPO_COLOR = { manuale: "bg-blue-100 text-blue-700", settimanale: "bg-emerald-100 text-emerald-700", "pre-ripristino": "bg-amber-100 text-amber-700" };

const GUIDA = [
  { icon: Clock, titolo: "1. Creazione automatica", testo: "Ogni lunedì alle 03:00 il sistema avvia da solo un backup completo dei dati dell'azienda (ospiti, turni, consegne, movimenti, firme, documenti-anagrafica, ecc.). Puoi anche crearne uno manuale in qualsiasi momento con il pulsante «Crea backup ora»." },
  { icon: CloudUpload, titolo: "2. Dove finisce", testo: "I dati vengono esportati e compressi in un unico file .zip, caricato nell'archivio cloud persistente (separato dal database: se il database si corrompe o l'app si riavvia, il backup resta intatto). I file/PDF restano nel loro archivio cloud." },
  { icon: ListChecks, titolo: "3. Conservazione", testo: "Vengono mantenute le ultime N copie (configurabile qui sotto, di default 12). Le più vecchie vengono eliminate automaticamente, così non si accumula all'infinito." },
  { icon: HardDriveDownload, titolo: "4. Copia tua (off-site)", testo: "Da questa pagina puoi scaricare qualsiasi backup sul tuo dispositivo (PC, Google Drive aziendale…). Così una copia vive anche FUORI dalla piattaforma: è la rete di sicurezza indipendente." },
  { icon: Mail, titolo: "5. Backup via e-mail", testo: "Ogni backup automatico settimanale arriva via e-mail agli amministratori con un pulsante «Scarica il backup». Il link apre l'app e, per sicurezza, il download parte solo dopo l'accesso come amministratore. Puoi anche inviare via e-mail un backup specifico in qualsiasi momento con il pulsante «Invia via email» qui sotto. I passi: 1) apri la mail, 2) premi «Scarica il backup», 3) accedi se richiesto, 4) salva il file .zip in un luogo sicuro." },
  { icon: RotateCcw, titolo: "6. Ripristino (se mai servisse)", testo: "Puoi ripristinare un backup: il sistema crea PRIMA un backup di sicurezza dello stato attuale, poi ricarica i dati scelti. Richiede conferma scritta. Operazione delicata: usala solo in caso di reale necessità." },
];

export default function DatabasePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [cfg, setCfg] = useState({ retention: 12, enabled: true, email_notifica: true });
  const [restore, setRestore] = useState(null);
  const [confirmTxt, setConfirmTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(null);
  const [fileRestore, setFileRestore] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [fileConfirmTxt, setFileConfirmTxt] = useState("");
  const [fileBusy, setFileBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const [l, c] = await Promise.all([api.get("/backup/list"), api.get("/backup/config")]);
      setRows(l.data || []);
      setCfg(c.data || { retention: 12, enabled: true });
    } catch { toast.error("Errore nel caricamento"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Download automatico quando si arriva dal link email (?scarica=<id>)
  useEffect(() => {
    const bid = searchParams.get("scarica");
    if (!bid || loading) return;
    const b = rows.find((x) => x.id === bid);
    if (b && b.status !== "errore") {
      scarica(b);
      toast.success("Download del backup avviato");
    } else {
      toast.error("Backup non trovato o non più disponibile");
    }
    searchParams.delete("scarica");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, rows]);

  const creaOra = async () => {
    setRunning(true);
    try { await api.post("/backup/run"); toast.success("Backup creato con successo"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nella creazione del backup"); }
    setRunning(false);
  };
  const salvaCfg = async (next) => {
    setCfg(next);
    try { await api.put("/backup/config", next); toast.success("Impostazioni salvate"); }
    catch { toast.error("Errore nel salvataggio"); }
  };
  const scarica = async (b) => {
    try {
      const res = await api.get(`/backup/${b.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = b.filename || "backup.zip";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Errore nel download"); }
  };
  const inviaEmail = async (b) => {
    setEmailing(b.id);
    try {
      const res = await api.post(`/backup/${b.id}/send-email`);
      const n = (res.data?.destinatari || []).length;
      toast.success(`Email inviata${n ? ` a ${n} destinatario/i` : ""} con il link di download`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'invio dell'email"); }
    setEmailing(null);
  };
  const elimina = async (b) => {
    if (!window.confirm("Eliminare questo backup? L'operazione non è reversibile.")) return;
    try { await api.delete(`/backup/${b.id}`); toast.success("Backup eliminato"); setRows((p) => p.filter((x) => x.id !== b.id)); }
    catch { toast.error("Errore"); }
  };
  const eseguiRipristino = async () => {
    if (confirmTxt !== "RIPRISTINA") { toast.error("Digita RIPRISTINA per confermare"); return; }
    setBusy(true);
    try {
      await api.post(`/backup/${restore.id}/restore`, { conferma: "RIPRISTINA" });
      toast.success("Ripristino completato");
      setRestore(null); setConfirmTxt(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel ripristino"); }
    setBusy(false);
  };
  const eseguiRipristinoFile = async () => {
    if (fileConfirmTxt !== "RIPRISTINA") { toast.error("Digita RIPRISTINA per confermare"); return; }
    if (!restoreFile) { toast.error("Seleziona il file .zip del backup"); return; }
    setFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      fd.append("conferma", "RIPRISTINA");
      const res = await api.post("/backup/restore-da-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = Object.keys(res.data?.restored || {}).length;
      toast.success(`Ripristino completato da file (${n} collezioni)`);
      setFileRestore(false); setRestoreFile(null); setFileConfirmTxt(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel ripristino da file"); }
    setFileBusy(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="database-page">
      <div className="flex items-center gap-3">
        <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-sky-100 text-sky-600"><Database size={22} /></span>
        <div>
          <h1 className="text-2xl font-head font-bold">Database & Backup</h1>
          <p className="text-sm text-muted-foreground">Copie di sicurezza automatiche dei tuoi dati, per non perderli mai.</p>
        </div>
      </div>

      {/* Guida */}
      <div className="gr-card p-6" data-testid="database-guida">
        <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-emerald-600" /><h2 className="font-head font-bold text-lg">Come funziona — la guida</h2></div>
        <div className="space-y-4">
          {GUIDA.map((g, i) => {
            const Ic = g.icon;
            return (
              <div key={i} className="flex gap-3">
                <span className="inline-grid place-items-center h-8 w-8 rounded-lg bg-slate-100 text-slate-600 shrink-0"><Ic size={16} /></span>
                <div><div className="font-semibold text-sm">{g.titolo}</div><div className="text-sm text-muted-foreground">{g.testo}</div></div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Impostazioni + crea ora */}
      <div className="gr-card p-6 space-y-4" data-testid="database-impostazioni">
        <h2 className="font-head font-bold text-lg">Impostazioni backup</h2>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-semibold text-sm">Backup automatico settimanale</div>
            <div className="text-xs text-muted-foreground">Ogni lunedì alle 03:00 (ora italiana)</div>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => salvaCfg({ ...cfg, enabled: v })} data-testid="backup-auto-switch" />
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap border-t pt-4">
          <div>
            <div className="font-semibold text-sm">Email di conferma settimanale</div>
            <div className="text-xs text-muted-foreground">Ricevi ogni settimana una mail di conferma «backup eseguito» (con data e dimensione)</div>
          </div>
          <Switch checked={cfg.email_notifica !== false} onCheckedChange={(v) => salvaCfg({ ...cfg, email_notifica: v })} data-testid="backup-email-switch" />
        </div>
        <div className="flex items-center justify-between gap-4 flex-wrap border-t pt-4">
          <div>
            <div className="font-semibold text-sm">Copie da conservare</div>
            <div className="text-xs text-muted-foreground">Oltre questo numero, i backup più vecchi vengono eliminati automaticamente</div>
          </div>
          <Input type="number" min={1} max={60} value={cfg.retention} onChange={(e) => setCfg({ ...cfg, retention: e.target.value })} onBlur={(e) => salvaCfg({ ...cfg, retention: Math.max(1, Math.min(60, parseInt(e.target.value) || 12)) })} className="w-24" data-testid="backup-retention-input" />
        </div>
        <div className="border-t pt-4">
          <Button onClick={creaOra} disabled={running} className="font-semibold" data-testid="backup-run-btn">
            <Play size={16} className="mr-1.5" /> {running ? "Creazione in corso…" : "Crea backup ora"}
          </Button>
        </div>
      </div>

      {/* Guida di emergenza: cosa fare se il sistema si compromette */}
      <div className="gr-card p-6 border-amber-200 bg-amber-50/40" data-testid="database-guida-emergenza">
        <div className="flex items-center gap-2 mb-3"><LifeBuoy size={18} className="text-amber-600" /><h2 className="font-head font-bold text-lg">Emergenza — come rimettere i dati come prima</h2></div>
        <p className="text-sm text-muted-foreground mb-4">Se qualcosa va storto, puoi riportare i dati esattamente a com'erano al momento di un backup. Scegli lo scenario:</p>

        <div className="rounded-xl bg-white border p-4 mb-3">
          <div className="font-semibold text-sm flex items-center gap-2 mb-1"><RotateCcw size={15} className="text-emerald-600" /> Scenario A — I dati sono danneggiati ma l'app funziona</div>
          <ol className="text-sm text-muted-foreground list-decimal ml-5 space-y-0.5">
            <li>Vai qui in <b>Database &amp; Backup</b> (già ci sei).</li>
            <li>Nell'elenco «Backup disponibili» scegli la copia da cui ripartire (es. l'ultima buona).</li>
            <li>Premi <b>Ripristina</b> → digita <b>RIPRISTINA</b> → conferma.</li>
            <li>Il sistema crea prima un backup di sicurezza dello stato attuale, poi rimette i dati del backup scelto.</li>
          </ol>
        </div>

        <div className="rounded-xl bg-white border p-4">
          <div className="font-semibold text-sm flex items-center gap-2 mb-1"><UploadCloud size={15} className="text-sky-600" /> Scenario B — Sistema compromesso / si riparte da zero</div>
          <p className="text-sm text-muted-foreground mb-1">In questo caso l'elenco dei backup potrebbe essere vuoto: userai la <b>copia .zip che hai scaricato sul tuo PC / Google Drive</b> (quella ricevuta via e-mail o scaricata da qui). Ecco i passi:</p>
          <ol className="text-sm text-muted-foreground list-decimal ml-5 space-y-0.5">
            <li>Accedi all'app come <b>amministratore</b>.</li>
            <li>Apri <b>Configurazione → Database &amp; Backup</b>.</li>
            <li>Premi il pulsante <b>«Ripristina da file»</b> qui sotto.</li>
            <li>Seleziona il file <b>.zip</b> del backup salvato sul tuo dispositivo.</li>
            <li>Digita <b>RIPRISTINA</b> e conferma. I dati tornano identici a quel backup.</li>
          </ol>
          <p className="text-xs text-muted-foreground mt-2">Nota: conserva sempre almeno un file .zip recente fuori dalla piattaforma (PC, Google Drive, chiavetta). È la tua rete di sicurezza definitiva.</p>
          <div className="mt-3">
            <Button variant="outline" className="font-semibold border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => { setFileRestore(true); setRestoreFile(null); setFileConfirmTxt(""); }} data-testid="backup-restore-file-btn">
              <UploadCloud size={16} className="mr-1.5" /> Ripristina da file
            </Button>
          </div>
        </div>
      </div>

      {/* Elenco backup */}
      <div className="gr-card p-6" data-testid="database-elenco">
        <h2 className="font-head font-bold text-lg mb-3">Backup disponibili</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Caricamento…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="backup-empty">Nessun backup presente. Creane uno con «Crea backup ora».</p>
        ) : (
          <div className="space-y-2" data-testid="backup-list">
            {rows.map((b) => (
              <div key={b.id} className="border rounded-lg p-3 flex items-center gap-3 flex-wrap" data-testid={`backup-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                    {fmtWhen(b.created_at)}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${TIPO_COLOR[b.tipo] || "bg-slate-100 text-slate-600"}`}>{TIPO_LABEL[b.tipo] || b.tipo}</span>
                    {b.status === "errore" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1"><AlertTriangle size={10} /> errore</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {b.status === "errore" ? (b.errore || "Backup non riuscito") : <>{(b.total || 0).toLocaleString("it-IT")} record · {fmtSize(b.size)} · creato da {b.created_by || "—"}</>}
                  </div>
                </div>
                {b.status !== "errore" && (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => scarica(b)} data-testid={`backup-download-${b.id}`}><Download size={14} className="mr-1" /> Scarica</Button>
                    <Button size="sm" variant="outline" onClick={() => inviaEmail(b)} disabled={emailing === b.id} data-testid={`backup-email-${b.id}`}><Mail size={14} className="mr-1" /> {emailing === b.id ? "Invio…" : "Invia via email"}</Button>
                    <Button size="sm" variant="outline" onClick={() => { setRestore(b); setConfirmTxt(""); }} data-testid={`backup-restore-${b.id}`}><RotateCcw size={14} className="mr-1" /> Ripristina</Button>
                    <Button size="sm" variant="outline" className="text-destructive" onClick={() => elimina(b)} data-testid={`backup-delete-${b.id}`}><Trash2 size={14} /></Button>
                  </div>
                )}
                {b.status === "errore" && (
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => elimina(b)} data-testid={`backup-delete-${b.id}`}><Trash2 size={14} /></Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog ripristino */}
      <Dialog open={!!restore} onOpenChange={(o) => { if (!o) { setRestore(null); setConfirmTxt(""); } }}>
        <DialogContent data-testid="backup-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle size={18} /> Ripristina backup</DialogTitle>
            <DialogDescription>
              Stai per ripristinare il backup del <b>{restore ? fmtWhen(restore.created_at) : ""}</b>. I dati attuali dell'azienda verranno sostituiti con quelli del backup.
              Verrà creato automaticamente un <b>backup di sicurezza</b> dello stato attuale prima di procedere.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm">Per confermare, digita <b>RIPRISTINA</b> qui sotto:</p>
            <Input value={confirmTxt} onChange={(e) => setConfirmTxt(e.target.value)} placeholder="RIPRISTINA" data-testid="backup-restore-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestore(null); setConfirmTxt(""); }}>Annulla</Button>
            <Button onClick={eseguiRipristino} disabled={busy || confirmTxt !== "RIPRISTINA"} className="bg-amber-600 hover:bg-amber-700" data-testid="backup-restore-confirm-btn">
              {busy ? "Ripristino…" : "Conferma ripristino"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ripristino da file */}
      <Dialog open={fileRestore} onOpenChange={(o) => { if (!o) { setFileRestore(false); setRestoreFile(null); setFileConfirmTxt(""); } }}>
        <DialogContent data-testid="backup-restore-file-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sky-700"><UploadCloud size={18} /> Ripristina da file</DialogTitle>
            <DialogDescription>
              Carica un file <b>.zip</b> di backup salvato in precedenza (sul tuo PC, Google Drive, ecc.). I dati attuali dell'azienda verranno
              sostituiti con quelli contenuti nel file. Verrà creato automaticamente un <b>backup di sicurezza</b> dello stato attuale prima di procedere.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-semibold">File di backup (.zip)</label>
              <input type="file" accept=".zip,application/zip" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="mt-1 block w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-sky-100 file:text-sky-700 file:font-semibold file:cursor-pointer" data-testid="backup-restore-file-input" />
              {restoreFile && <p className="text-xs text-muted-foreground mt-1">Selezionato: <b>{restoreFile.name}</b> ({fmtSize(restoreFile.size)})</p>}
            </div>
            <div>
              <p className="text-sm mb-1">Per confermare, digita <b>RIPRISTINA</b>:</p>
              <Input value={fileConfirmTxt} onChange={(e) => setFileConfirmTxt(e.target.value)} placeholder="RIPRISTINA" data-testid="backup-restore-file-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFileRestore(false); setRestoreFile(null); setFileConfirmTxt(""); }}>Annulla</Button>
            <Button onClick={eseguiRipristinoFile} disabled={fileBusy || !restoreFile || fileConfirmTxt !== "RIPRISTINA"} className="bg-sky-600 hover:bg-sky-700" data-testid="backup-restore-file-confirm-btn">
              {fileBusy ? "Ripristino…" : "Ripristina da file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
